/**
 * Settings Service
 *
 * Manages user settings including timezone preferences
 */

import { supabase, getCurrentUserId } from './supabaseClient';

// In-memory cache for settings (survives navigation within session)
let settingsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes - settings rarely change during a session
// Cache persists for entire session - only cleared on page refresh or manual changes

/**
 * Detect user's timezone from browser
 * @returns {string} IANA timezone string (e.g., 'America/New_York')
 */
export const detectUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error('Error detecting timezone:', error);
    return 'America/New_York'; // Default fallback
  }
};

/**
 * Get user settings, creating default settings if they don't exist
 * @returns {Promise<Object>} User settings object
 */
export const getUserSettings = async (forceRefresh = false) => {
  try {
    // Return cached settings if valid and not forcing refresh
    if (!forceRefresh && settingsCache && cacheTimestamp) {
      const cacheAge = Date.now() - cacheTimestamp;
      if (cacheAge < CACHE_DURATION) {
        console.log(`⚡ Using cached settings (age: ${Math.round(cacheAge / 1000)}s)`);
        return settingsCache;
      }
    }

    const userId = await getCurrentUserId();
    const detectedTimezone = detectUserTimezone();

    console.log(`📥 Fetching user settings for user: ${userId}`);

    // Try to get existing settings
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .single();

    console.log(`   Query result - data:`, data ? 'found' : 'null', `error:`, error);

    // Handle different error cases
    if (error) {
      if (error.code === 'PGRST116') {
        // Record not found - this is expected for new users
        console.warn(`⚠️  No existing settings found (PGRST116), creating new record...`);
      } else {
        // Unexpected error - log and throw
        console.error('❌ Error fetching user settings:', error);
        throw error;
      }
    }

    // If settings don't exist, create them with detected timezone
    if (!data) {
      console.warn(`⚠️  Creating default settings for user ${userId}...`);
      const defaultSettings = {
        user_id: userId,
        timezone: detectedTimezone,
        date_format: 'MMM DD, YYYY',
        currency_display: 'symbol',
        theme: 'light',
        tier3_llm_enabled: false, // Default: Tier-3 LLM disabled
      };

      const { data: newSettings, error: insertError } = await supabase
        .from('user_settings')
        .insert(defaultSettings)
        .select()
        .single();

      if (insertError) {
        console.error('Error creating user settings:', insertError);
        throw insertError;
      }

      console.log(`✅ Created default settings with timezone: ${detectedTimezone}`);

      // Cache the new settings
      settingsCache = newSettings;
      cacheTimestamp = Date.now();

      return newSettings;
    }

    console.log(`✅ Retrieved user settings`);

    // Cache the settings
    settingsCache = data;
    cacheTimestamp = Date.now();

    // Auto-update timezone if browser timezone changed
    if (data.timezone !== detectedTimezone) {
      console.log(`🔄 Browser timezone changed from ${data.timezone} to ${detectedTimezone}, updating...`);

      const { data: updatedSettings, error: updateError } = await supabase
        .from('user_settings')
        .update({ timezone: detectedTimezone })
        .eq('user_id', userId)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating timezone:', updateError);
        // Return old data with detected timezone as fallback
        return { ...data, timezone: detectedTimezone };
      }

      // Update cache
      settingsCache = updatedSettings;
      cacheTimestamp = Date.now();

      return updatedSettings;
    }

    return data;
  } catch (error) {
    console.error('getUserSettings error:', error);
    throw error;
  }
};

/**
 * Update user settings
 * @param {Object} updates - Settings to update
 * @returns {Promise<Object>} Updated settings object
 */
export const updateUserSettings = async (updates) => {
  try {
    const userId = await getCurrentUserId();

    const { data, error} = await supabase
      .from('user_settings')
      .update(updates)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating user settings:', error);
      throw error;
    }

    console.log(`✅ Updated user settings:`, updates);

    // Update cache immediately
    settingsCache = data;
    cacheTimestamp = Date.now();

    return data;
  } catch (error) {
    console.error('updateUserSettings error:', error);
    throw error;
  }
};

/**
 * Update Tier-3 LLM enabled setting
 * @param {boolean} enabled - Enable or disable Tier-3 LLM classification
 * @returns {Promise<Object>} Updated settings object
 */
export const updateTier3LLMSetting = async (enabled) => {
  try {
    const userId = await getCurrentUserId();

    console.log(`🧠 Updating Tier-3 LLM setting...`);

    // First, ensure user_settings record exists by calling getUserSettings
    // This will create the record if it doesn't exist
    // IMPORTANT: Force refresh to avoid using stale cache
    const existingSettings = await getUserSettings(true);  // forceRefresh = true

    // Use simple update since we know the record exists (we just fetched it)
    const { data, error } = await supabase
      .from('user_settings')
      .update({ tier3_llm_enabled: enabled })
      .eq('user_id', userId)
      .select()
      .single();


    if (error) {
      console.error('❌ Supabase error updating Tier-3 LLM setting:', error);
      console.error('   Error details:', JSON.stringify(error, null, 2));
      throw error;
    }

    if (!data) {
      console.error('❌ Update returned no data - record may not exist');
      throw new Error('Failed to update tier3_llm_enabled - no data returned');
    }


    // Update cache immediately for instant UI feedback
    settingsCache = data;
    cacheTimestamp = Date.now();

    return data;
  } catch (error) {
    console.error('❌ updateTier3LLMSetting error:', error);
    console.error('   Error type:', error.name);
    console.error('   Error message:', error.message);
    throw error;
  }
};

/**
 * Common timezone options for the dropdown
 */
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZDT/NZST)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

/**
 * Format date using user's timezone preference
 * @param {string|Date} date - Date to format
 * @param {string} timezone - IANA timezone string
 * @param {string} format - Date format ('short', 'long', 'full')
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, timezone, format = 'short') => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    let options = {};
    switch (format) {
      case 'short':
        options = { month: 'short', day: 'numeric', year: 'numeric', timeZone: timezone };
        break;
      case 'long':
        options = { month: 'long', day: 'numeric', year: 'numeric', timeZone: timezone };
        break;
      case 'full':
        options = {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: timezone
        };
        break;
      default:
        options = { month: 'short', day: 'numeric', year: 'numeric', timeZone: timezone };
    }

    return dateObj.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    return date.toString();
  }
};

/**
 * Convert date to user's timezone and return YYYY-MM-DD format
 * This is used for storing dates in the database
 * @param {string|Date} date - Date to convert
 * @param {string} timezone - IANA timezone string
 * @returns {string} Date in YYYY-MM-DD format in user's timezone
 */
export const getDateInTimezone = (date, timezone) => {
  if (!date) return null;

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  try {
    // Get the date parts in the user's timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone,
    });

    const parts = formatter.formatToParts(dateObj);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error getting date in timezone:', error);
    return dateObj.toISOString().split('T')[0];
  }
};

/**
 * Format a YYYY-MM-DD date string for display without timezone shift
 * This parses the date as a LOCAL date (not UTC) to prevent timezone conversion issues
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {string} format - Format style: 'short' (e.g., 'Oct 20'), 'long' (e.g., 'Oct 20, 2025')
 * @returns {string} Formatted date string
 */
export const formatLocalDateString = (dateString, format = 'short') => {
  if (!dateString) return 'Not set';

  try {
    // Parse as local date components (not UTC) to avoid timezone shift
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed

    if (isNaN(date.getTime())) {
      console.error('Invalid date string:', dateString);
      return dateString;
    }

    const options = format === 'short'
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };

    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting local date string:', error);
    return dateString;
  }
};
